/**
 * Every purchase order in one list, with the payable each one queued, so it is
 * easy to see what has been ordered and what finance still owes.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Download, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { peso, shortDate } from '../../lib/format';
import {
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';
import { downloadPo, type PurchaseOrder } from './PurchaseOrdersCard';

const AP_TONE: Record<string, 'ok' | 'warning' | 'critical' | 'neutral'> = {
  COMPLETED: 'ok',
  PENDING: 'warning',
  DISAPPROVED: 'critical',
  CANCELLED: 'neutral',
};

export function PurchaseOrdersPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ISSUED');

  const params = new URLSearchParams({ pageSize: '100' });
  if (search) params.set('q', search);
  if (status) params.set('status', status);

  const orders = useQuery({
    queryKey: ['purchase-orders', 'list', params.toString()],
    queryFn: () => api<{ data: PurchaseOrder[]; total: number }>(`/purchase-orders?${params}`),
  });

  const rows = orders.data?.data ?? [];
  const outstanding = rows
    .filter((row) => row.status === 'ISSUED' && row.apEntry?.status === 'PENDING')
    .reduce((total, row) => total + Number(row.total), 0);

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-navy dark:text-white">Purchase orders</h1>
        <p className="text-sm text-slate-500">
          Raised from a sales order. Each one queues a payable, charged to the project.
        </p>
      </header>

      <Card>
        <div className="flex flex-wrap gap-3">
          <label className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              className={`${inputClass} pl-9`}
              placeholder="Search PO number, supplier, sales order or job"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search purchase orders"
            />
          </label>
          <select
            className={`${inputClass} w-44`}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Filter by status"
          >
            <option value="ISSUED">Issued</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="">All</option>
          </select>
        </div>
      </Card>

      {orders.isLoading && <Skeleton className="h-64 w-full" />}

      {!orders.isLoading && rows.length === 0 && (
        <Card>
          <EmptyState
            title="No purchase orders"
            description="Open a sales order and issue one from there — a PO needs a confirmed job to belong to."
          />
        </Card>
      )}

      {rows.length > 0 && (
        <>
          {outstanding > 0 && (
            <Card className="bg-navy text-white dark:bg-navy">
              <p className="text-xs uppercase tracking-wide text-white/70">
                Ordered and not yet paid
              </p>
              <p className="tabular text-lg font-bold">{peso(outstanding.toFixed(2))}</p>
            </Card>
          )}

          <Card>
            <TableWrap>
              <thead>
                <tr>
                  <Th>PO</Th>
                  <Th>Supplier</Th>
                  <Th>For</Th>
                  <Th>Payment</Th>
                  <Th align="right">Total</Th>
                  <Th>Payable</Th>
                  <Th>{''}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <Td>
                      <p className="font-mono text-xs font-semibold">{row.code}</p>
                      <p className="text-xs text-slate-500">{shortDate(row.poDate)}</p>
                      {row.status === 'CANCELLED' && (
                        <StatusChip tone="critical">Cancelled</StatusChip>
                      )}
                    </Td>
                    <Td>
                      <p>{row.supplier.name}</p>
                      {row.description && (
                        <p className="text-xs text-slate-500">{row.description}</p>
                      )}
                    </Td>
                    <Td>
                      <p className="text-xs">{row.project?.name ?? row.salesOrder.projectName}</p>
                      <p className="text-xs text-slate-500">{row.salesOrder.customer.name}</p>
                      <Link
                        to={`/sales-orders/${row.salesOrder.id}`}
                        className="font-mono text-xs text-brand hover:underline"
                      >
                        {row.salesOrder.code}
                      </Link>
                    </Td>
                    <Td>
                      <p className="text-xs">{row.paymentMode?.label ?? 'To be advised'}</p>
                      {row.paymentDate && (
                        <p className="text-xs text-slate-500">{shortDate(row.paymentDate)}</p>
                      )}
                    </Td>
                    <Td align="right">
                      {peso(row.total)}
                      <span className="block text-xs text-slate-500">
                        {peso(row.amountVatEx)} + {peso(row.vatAmount)} VAT
                      </span>
                    </Td>
                    <Td>
                      {row.apEntry ? (
                        <Link to={`/payables?q=${row.apEntry.code}`}>
                          <StatusChip tone={AP_TONE[row.apEntry.status] ?? 'neutral'}>
                            {row.apEntry.code}
                          </StatusChip>
                        </Link>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </Td>
                    <Td>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                          aria-label={`Download ${row.code}`}
                          title="Download for signature"
                          onClick={() => downloadPo(row)}
                        >
                          <Download aria-hidden="true" className="h-4 w-4" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </Card>
        </>
      )}
    </div>
  );
}
